import { useLocation, useNavigate } from 'react-router-dom';
import { useView } from '../../contexts/ViewContext';
import { Chip, IconButton, Tooltip, Box } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Context Indicator
 * 
 * Shows current mode (Clinic Mode / Admin Mode) and impersonation status
 * Visible to super admins (or everyone if you prefer)
 */
const ContextIndicator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isViewingAs, viewMode, exitViewMode } = useView();
  
  // Show for everyone, or just super admins - adjust as needed
  // For now, showing for everyone for clarity
  // const { isSuperAdmin } = useAuth();
  // if (!isSuperAdmin) return null;
  
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isClinicalRoute = location.pathname.startsWith('/clinic-dashboard') || 
                          location.pathname.startsWith('/clinic-staff-dashboard') ||
                          location.pathname.startsWith('/physician') ||
                          location.pathname.startsWith('/dashboard') ||
                          location.pathname.startsWith('/sponsor-dashboard');
  
  // Show impersonation status if active (highest priority)
  if (isViewingAs && viewMode.active) {
    const roleLabels = {
      patient: 'Patient',
      physician: 'Physician',
      clinic_staff: 'Clinic Staff',
      sponsor: 'Sponsor',
      clinic: 'Clinic'
    };
    
    const roleLabel = roleLabels[viewMode.userType] || viewMode.userType;
    const modeLabel = isAdminRoute ? 'Admin Mode' : 'Clinic Mode';
    
    const handleExit = () => {
      exitViewMode();
      navigate('/admin'); // Return to admin dashboard
    };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          icon={<PersonIcon fontSize="small" />}
          label={`${modeLabel} | Viewing as: ${roleLabel}`}
          color="warning"
          size="small"
          sx={{ 
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        />
        <Tooltip title="Exit impersonation">
          <IconButton
            size="small"
            onClick={handleExit}
            sx={{
              color: 'white',
              padding: '2px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }
  
  // Show mode indicator
  if (isAdminRoute) {
    return (
      <Chip
        icon={<AdminPanelSettingsIcon fontSize="small" />}
        label="Admin Mode"
        color="primary"
        size="small"
        sx={{ 
          fontSize: '0.75rem',
          fontWeight: 500,
        }}
      />
    );
  }
  
  if (isClinicalRoute) {
    return (
      <Chip
        icon={<BusinessIcon fontSize="small" />}
        label="Clinic Mode"
        color="default"
        size="small"
        sx={{ 
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
        }}
      />
    );
  }
  
  return null;
};

export default ContextIndicator;
